import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Task 6 — Templates CRUD + UI-Overlay service.
 *
 * Consumes the Task-3 routes:
 *   GET    /v1/platform/templates              → TemplateKeyListResponse
 *   GET    /v1/platform/templates/{key}/{locale} → TemplateContent
 *   PUT    /v1/platform/templates/{key}/{locale} → 204
 *   DELETE /v1/platform/templates/{key}/{locale} → 204
 *   POST   /v1/platform/templates/{key}/preview  → TemplatePreviewResponse
 *   GET    /v1/content/ui?locale=<l>            → Record<string, string>
 *
 * All /platform/templates routes are platform-admin-gated (Bearer required).
 * /content/ui is intentionally unauthenticated.
 */

export interface TemplateLocale {
  locale: string;
  is_override: boolean;
}

export interface TemplateKey {
  key: string;
  type: 'email' | 'ui' | string;
  variables: string[];
  description: string;
  locales: TemplateLocale[];
}

export interface TemplateKeyListResponse {
  items: TemplateKey[];
  total: number;
}

export interface TemplateContent {
  key: string;
  type: string;
  subject?: string;
  body: string;
  is_override: boolean;
}

export interface TemplatePutPayload {
  subject?: string;
  body: string;
}

export interface TemplatePreviewResponse {
  subject?: string;
  body: string;
}

@Injectable({ providedIn: 'root' })
export class TemplatesService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/v1/platform/templates`;
  private contentBase = `${environment.apiBaseUrl}/v1/content`;

  /** List all template keys with per-locale override status. */
  listKeys(): Observable<TemplateKeyListResponse> {
    return this.http.get<TemplateKeyListResponse>(this.base);
  }

  /** Get resolved template content for a key+locale (tenant override takes precedence). */
  getContent(key: string, locale: string): Observable<TemplateContent> {
    return this.http.get<TemplateContent>(`${this.base}/${key}/${locale}`);
  }

  /** Upsert tenant override for a key+locale. Returns 204 on success. */
  putContent(key: string, locale: string, payload: TemplatePutPayload): Observable<void> {
    return this.http.put<void>(`${this.base}/${key}/${locale}`, payload);
  }

  /** Delete tenant override for a key+locale (revert to default). Returns 204 on success. */
  deleteContent(key: string, locale: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${key}/${locale}`);
  }

  /**
   * Preview rendered template for a key with given locale + vars.
   * On 400 unknown_variable the error propagates to the caller.
   */
  preview(key: string, payload: { locale: string; vars: Record<string, string> }): Observable<TemplatePreviewResponse> {
    return this.http.post<TemplatePreviewResponse>(`${this.base}/${key}/preview`, payload);
  }

  /**
   * Fetch the UI-overlay map for a given locale.
   * Returns {ngx-translate-key: body} pairs for type=ui templates.
   * No auth required — call from APP_INITIALIZER.
   *
   * Key-mapping convention: the seed `ui` template keys ARE the full ngx-translate keys
   * (e.g. key="i18n.fap.developer.signup_intro" → overlays
   * translate.get("i18n.fap.developer.signup_intro")). Platform admins
   * author UI templates using the exact translation key they want to override.
   */
  uiOverlay(locale: string): Observable<Record<string, string>> {
    return this.http.get<Record<string, string>>(`${this.contentBase}/ui`, {
      params: { locale },
    });
  }
}
